/**
 * Eval Disagreement Review Page (KAN-225)
 *
 * Admin-only page for reviewing and correcting human labels on emails
 * where the router and human classifier disagreed in the latest eval run.
 * Restricted to ben.clark@palace.cl.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/auth/AuthContext';
import {
  classificationBackfillService,
  INTENT_OPTIONS,
  type EvalDisagreement,
} from '../../services/api/classificationBackfillService';
import './EvalReview.css';

const ALLOWED_EMAIL = 'ben.clark@palace.cl';

const INTENT_LABEL: Record<string, string> = Object.fromEntries(
  INTENT_OPTIONS.map(o => [o.value, o.label])
);

function intentLabel(intent: string): string {
  return INTENT_LABEL[intent] ?? intent;
}

interface ItemState {
  selectedIntent: string;
  saving: boolean;
  saved: boolean;
  error: string | null;
  expanded: boolean;
}

const EvalReview: React.FC = () => {
  const { user } = useAuth();
  const isAllowed = user?.email === ALLOWED_EMAIL;

  const [runId, setRunId] = useState<string | null>(null);
  const [agreementRate, setAgreementRate] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [disagreements, setDisagreements] = useState<EvalDisagreement[]>([]);
  const [itemStates, setItemStates] = useState<Record<string, ItemState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAllowed) return;
    (async () => {
      try {
        const data = await classificationBackfillService.getLatestDisagreements();
        setRunId(data.runId);
        setAgreementRate(data.agreementRate);
        setTotal(data.total);
        setDisagreements(data.disagreements);
        // Initialise per-item state
        const states: Record<string, ItemState> = {};
        for (const d of data.disagreements) {
          states[d.messageId] = {
            selectedIntent: d.humanIntent,
            saving: false,
            saved: false,
            error: null,
            expanded: false,
          };
        }
        setItemStates(states);
      } catch (err: any) {
        setError(err.message || 'Failed to load disagreements');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAllowed]);

  const updateItem = (messageId: string, patch: Partial<ItemState>) => {
    setItemStates(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], ...patch },
    }));
  };

  const handleSave = async (d: EvalDisagreement) => {
    const state = itemStates[d.messageId];
    if (!state || state.saving) return;
    updateItem(d.messageId, { saving: true, error: null });
    try {
      await classificationBackfillService.relabel(d.messageId, state.selectedIntent);
      updateItem(d.messageId, { saving: false, saved: true });
    } catch (err: any) {
      updateItem(d.messageId, { saving: false, error: err.message || 'Save failed' });
    }
  };

  if (!isAllowed) {
    return (
      <div className="eval-review-page">
        <div className="eval-review-centered">Access restricted.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="eval-review-page">
        <div className="eval-review-centered">Loading disagreements...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="eval-review-page">
        <div className="eval-review-centered eval-review-error">{error}</div>
      </div>
    );
  }

  const relabelledCount = Object.values(itemStates).filter(s => s.saved).length;

  return (
    <div className="eval-review-page">
      <div className="eval-review-header">
        <div>
          <h1>Eval Disagreement Review</h1>
          {runId && (
            <p className="eval-review-subtitle">
              Run {runId} &mdash; {agreementRate}% agreement ({total - disagreements.length}/{total} correct)
            </p>
          )}
        </div>
        <div className="eval-review-summary">
          <span className="eval-review-badge">{disagreements.length} disagreements</span>
          {relabelledCount > 0 && (
            <span className="eval-review-badge eval-review-badge--saved">
              {relabelledCount} relabelled
            </span>
          )}
        </div>
      </div>

      {disagreements.length === 0 && (
        <div className="eval-review-centered">No disagreements — router agrees with all human labels.</div>
      )}

      <div className="eval-review-list">
        {disagreements.map((d) => {
          const state = itemStates[d.messageId];
          if (!state) return null;
          const isDirty = state.selectedIntent !== d.humanIntent;
          const confidencePct = Math.round(d.routerConfidence * 100);

          return (
            <div key={d.messageId} className={`eval-review-card ${state.saved ? 'eval-review-card--saved' : ''}`}>
              {/* Card header */}
              <div className="eval-review-card-header">
                <div className="eval-review-card-meta">
                  <span className="eval-review-bank">{d.bankName || '(unknown bank)'}</span>
                  <span className="eval-review-subject" title={d.subject}>{d.subject}</span>
                </div>
                <button
                  className="eval-review-expand-btn"
                  onClick={() => updateItem(d.messageId, { expanded: !state.expanded })}
                >
                  {state.expanded ? 'Hide body' : 'Show body'}
                </button>
              </div>

              {/* Intent comparison */}
              <div className="eval-review-intents">
                <div className="eval-review-intent eval-review-intent--human">
                  <span className="eval-review-intent-label">Human</span>
                  <span className="eval-review-intent-value">{intentLabel(d.humanIntent)}</span>
                </div>
                <div className="eval-review-intent-arrow">→</div>
                <div className="eval-review-intent eval-review-intent--router">
                  <span className="eval-review-intent-label">Router ({confidencePct}%)</span>
                  <span className="eval-review-intent-value">{intentLabel(d.routerIntent)}</span>
                </div>
              </div>

              {/* Rationale */}
              <div className="eval-review-rationale">
                <span className="eval-review-rationale-label">Rationale: </span>
                {d.routerRationale}
              </div>

              {/* Expandable email body */}
              {state.expanded && (
                <div className="eval-review-body-section">
                  {d.body && (
                    <div className="eval-review-body-block">
                      <div className="eval-review-body-title">Email body</div>
                      <pre className="eval-review-body-text">{d.body}</pre>
                    </div>
                  )}
                  {d.parsedContent && (
                    <div className="eval-review-body-block">
                      <div className="eval-review-body-title">Parsed attachment content</div>
                      <pre className="eval-review-body-text">{d.parsedContent}</pre>
                    </div>
                  )}
                  {d.attachmentNames.length > 0 && (
                    <div className="eval-review-attachments">
                      Attachments: {d.attachmentNames.join(', ')}
                    </div>
                  )}
                  {!d.body && !d.parsedContent && (
                    <div className="eval-review-no-body">No body content available.</div>
                  )}
                </div>
              )}

              {/* Relabel controls */}
              <div className="eval-review-controls">
                <select
                  className="eval-review-select"
                  value={state.selectedIntent}
                  onChange={e => updateItem(d.messageId, { selectedIntent: e.target.value, saved: false })}
                  disabled={state.saving}
                >
                  {INTENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  className={`eval-review-save-btn ${isDirty ? 'eval-review-save-btn--dirty' : ''}`}
                  onClick={() => handleSave(d)}
                  disabled={state.saving || state.saved}
                >
                  {state.saving ? 'Saving...' : state.saved ? 'Saved' : isDirty ? 'Save change' : 'Keep human label'}
                </button>
                {!isDirty && !state.saved && (
                  <button
                    className="eval-review-accept-btn"
                    onClick={() => {
                      updateItem(d.messageId, { selectedIntent: d.routerIntent });
                    }}
                  >
                    Accept router
                  </button>
                )}
                {state.error && <span className="eval-review-item-error">{state.error}</span>}
                {state.saved && <span className="eval-review-saved-indicator">✓</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EvalReview;
